import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { useCapture } from '../capture/CaptureContext';
import { invalidateEntity } from '../api/queries';
import * as api from '../api';
import { reportProposalOutcome } from '../api';
import { applyMeetingReport } from './applyMeetingReport';
import { renderText, subjectFor } from './meetingReport';
import { streamChat } from './stream';
import { applyProposal, undoProposal, canUndo } from './apply';
import { editProposalField } from './editable';
import { today } from '../capture/dates';
import { loadDraft, saveDraft, clearDraft } from '../capture/draftStorage';

// The assistant's conversation, app-wide, so the drawer survives navigation.
// Messages persist for the browser session; the unsent words in the composer
// persist for good, so nothing typed is ever lost. Every write the assistant
// proposes is applied here, through the ordinary routes, only on approval —
// and what the person did with each card goes back to the model as a line in
// the transcript, so later turns know what exists.

const AssistantContext = createContext(null);
const NO_CONTEXT = {
  open: false, messages: [], busy: false, draft: '', setDraft: () => {}, width: 504, setWidth: () => {},
  toggle: () => {}, openWith: () => {}, close: () => {}, send: () => {}, stop: () => {}, clear: () => {}, decide: async () => {}, applyMeeting: async () => null, dismissProposal: () => {},
  retry: () => {}, editFailed: () => {}, regenerate: () => {}, canRegenerate: () => false, setProposalAccepted: () => {}, saveMeetingDraft: () => {},
  editProposal: () => {}, viewing: null, clearViewing: () => {}, restoreViewing: () => {}, hint: null, focusTick: 0,
};

// v2: the default grew by a fifth; a width saved under v1 is not carried over.
const WIDTH_KEY = 'crm_assistant_width_v2';
const MIN_WIDTH = 320;
const MAX_WIDTH = 760;
const MAX_KEPT = 40;
const AUTOSAVE_MS = 300;
let seq = 0;
const nextId = () => `m${Date.now().toString(36)}${(seq += 1)}`;

const storageKey = (userId) => `crm_assistant_v1:${userId || 'anon'}`;
const load = (userId) => { try { const raw = sessionStorage.getItem(storageKey(userId)); const list = raw ? JSON.parse(raw) : []; return Array.isArray(list) ? list : []; } catch (e) { return []; } };
// Photos are not kept across a reload (too large); their names are.
const persist = (userId, messages) => {
  try {
    const slim = messages.slice(-MAX_KEPT).map((m) => (m.images ? { ...m, images: m.images.map((i) => ({ name: i.name })) } : m));
    sessionStorage.setItem(storageKey(userId), JSON.stringify(slim));
  } catch (e) { /* session storage unavailable */ }
};

const STATE_LABEL = { applied: 'Applied', rejected: 'Not now', failed: 'Could not apply', undone: 'Applied, then undone' };

const errorText = (err, fallback) => (err && err.response && err.response.data && (err.response.data.message || err.response.data.error)) || (err && err.message) || fallback;

/** What the model is told about a reply's cards once the person has decided. */
export function cardsLine(message) {
  const decided = (message.proposals || []).filter((p) => p.state && p.state !== 'pending');
  if (!decided.length) return '';
  return `\n\n[Cards: ${decided.map((p) => `${STATE_LABEL[p.state] || p.state}${p.edited ? ' after the person edited it' : ''} — ${p.kind}: "${p.title}"`).join('; ')}]`;
}

const MAX_DRAFT_CHARS = 3500;

/**
 * The meeting write-up the person is looking at, as words, so a revision
 * ("Shorter", "Customer version", or their own words) starts from the card
 * as they edited it rather than from the raw notes.
 */
export function meetingDraftLine(proposal) {
  const report = proposal.draftPayload || proposal.payload;
  if (!report) return '';
  let body = `Subject: ${subjectFor(report)}\nAudience: ${report.audience === 'customer' ? 'customer' : 'internal'}\n\n${renderText(report)}`;
  if (body.length > MAX_DRAFT_CHARS) body = `${body.slice(0, MAX_DRAFT_CHARS)}…`;
  return `\n\n[The meeting write-up card ${proposal.key} as it stands now${proposal.draftPayload ? ', with the person\'s own edits' : ''}. A revision starts from this and keeps their edits:\n${body}]`;
}

/** The transcript the server gets: words only, with photos described and cards accounted for. */
export function buildTranscript(messages) {
  // Only the newest write-up still waiting is sent in full; earlier drafts are history.
  let draftAt = -1;
  messages.forEach((m, i) => { if (m.role === 'assistant' && (m.proposals || []).some((p) => p.kind === 'meeting_report' && p.state === 'pending')) draftAt = i; });
  return messages
    .map((m, i) => ({ m, i }))
    .filter(({ m }) => !m.pending && ((m.text || '').trim() || (m.images && m.images.length) || (m.proposals && m.proposals.length)))
    .map(({ m, i }) => {
      if (m.role === 'user') {
        const n = m.images ? m.images.length : 0;
        return { role: 'user', content: n ? `${m.text || ''}\n\n(${n} photo${n === 1 ? ' was' : 's were'} attached)`.trim() : m.text };
      }
      const draft = i === draftAt ? meetingDraftLine([...m.proposals].reverse().find((p) => p.kind === 'meeting_report' && p.state === 'pending')) : '';
      return { role: 'assistant', content: `${m.text || ''}${cardsLine(m)}${draft}`.trim() };
    })
    .filter((t) => t.content);
}

export function AssistantProvider({ children }) {
  const { user } = useAuth();
  const { pageContext } = useCapture();
  const location = useLocation();
  const queryClient = useQueryClient();
  const toast = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;
  const userId = user ? user.id : null;
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(() => load(userId));
  // The list as it is now, for an Undo pressed after later renders.
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const [busy, setBusy] = useState(false);
  const [draft, setDraftState] = useState(() => { const d = loadDraft(userId); return d && d.text ? d.text : ''; });
  const [width, setWidthState] = useState(() => { try { const n = parseInt(localStorage.getItem(WIDTH_KEY), 10); return n >= MIN_WIDTH && n <= MAX_WIDTH ? n : 504; } catch (e) { return 504; } });
  const abortRef = useRef(null);
  // Photos as sent, by message, for a retry or a regenerate in this tab.
  // Too large for session storage, so a reload keeps only their names.
  const photosRef = useRef(new Map());
  // What "this" means: the record on screen, unless the person cleared it
  // for this page (to ask about something else without it leaning in).
  // A new page brings it back.
  const pageKey = pageContext ? JSON.stringify(pageContext) : null;
  const [clearedKey, setClearedKey] = useState(null);
  useEffect(() => { setClearedKey(null); }, [location.pathname]);
  const viewing = pageContext && pageKey !== clearedKey ? pageContext : null;
  const clearViewing = useCallback(() => setClearedKey(pageKey), [pageKey]);
  const restoreViewing = useCallback(() => setClearedKey(null), []);
  const pageRef = useRef({ pageContext: viewing, pathname: location.pathname });
  pageRef.current = { pageContext: viewing, pathname: location.pathname };
  // How the composer invites the next message ('note' after "Log a note"),
  // and a tick that asks the drawer to put the cursor in it.
  const [hint, setHint] = useState(null);
  const [focusTick, setFocusTick] = useState(0);

  useEffect(() => { setMessages(load(userId)); const d = loadDraft(userId); setDraftState(d && d.text ? d.text : ''); }, [userId]);
  useEffect(() => { persist(userId, messages); }, [userId, messages]);

  // The composer's words, saved as they are typed.
  useEffect(() => {
    const timer = setTimeout(() => { if (draft.trim()) saveDraft(userId, { text: draft }); else clearDraft(userId); }, AUTOSAVE_MS);
    return () => clearTimeout(timer);
  }, [draft, userId]);
  const setDraft = useCallback((text) => setDraftState(typeof text === 'function' ? text : String(text || '')), []);

  const setWidth = useCallback((w) => {
    const next = Math.min(Math.max(Math.round(w), MIN_WIDTH), MAX_WIDTH);
    setWidthState(next);
    try { localStorage.setItem(WIDTH_KEY, String(next)); } catch (e) { /* fine */ }
  }, []);

  const patchMessage = useCallback((id, fn) => setMessages((list) => list.map((m) => (m.id === id ? fn(m) : m))), []);

  /**
   * Ask for a reply to the conversation as it stands: `list` ends with the
   * person's message. `photos` are that message's attachments, if any.
   */
  const runReply = useCallback(async (list, photos = []) => {
    const userMsg = list[list.length - 1];
    const words = userMsg.text || '';
    const reply = { id: nextId(), role: 'assistant', text: '', tools: [], proposals: [], reports: [], pending: true, error: null, usage: null };
    const transcript = [...buildTranscript(list.slice(0, -1)), { role: 'user', content: words || '(see the attached photo)', images: photos.map((i) => ({ media_type: i.media_type, data: i.data })) }];
    setMessages([...list, reply]);
    setBusy(true);
    const controller = new AbortController();
    abortRef.current = controller;
    const { pageContext: page, pathname } = pageRef.current;
    const context = { path: pathname, account_id: page && page.account_id, opportunity_id: page && page.opportunity_id, contact_id: page && page.contact_id };
    try {
      await streamChat({
        messages: transcript, context, today: today(), signal: controller.signal,
        onEvent: (event) => {
          // A named step ("Reading your message") shows while nothing else is
          // moving; the first words or tool line of the round take its place.
          if (event.type === 'step') patchMessage(reply.id, (m) => ({ ...m, step: event.label, stepActive: true }));
          else if (event.type === 'text') patchMessage(reply.id, (m) => ({ ...m, text: m.text + event.text, stepActive: false }));
          else if (event.type === 'tool') patchMessage(reply.id, (m) => {
            const tools = [...m.tools];
            const i = tools.findIndex((t) => t.name === event.name && t.label === event.label && t.status === 'running');
            // A proposal that was checked gives way to its card.
            if (event.status === 'settled') { if (i >= 0) tools.splice(i, 1); return { ...m, tools, stepActive: false }; }
            const entry = { name: event.name, label: event.label, status: event.status, detail: event.detail || null };
            if (i >= 0 && event.status !== 'running') tools[i] = entry; else tools.push(entry);
            return { ...m, tools, stepActive: false };
          });
          else if (event.type === 'proposal') patchMessage(reply.id, (m) => ({ ...m, proposals: [...m.proposals, { ...event.proposal, accepted: true, state: 'pending' }] }));
          else if (event.type === 'report') patchMessage(reply.id, (m) => ({ ...m, reports: [...(m.reports || []), event.report] }));
          else if (event.type === 'error') patchMessage(reply.id, (m) => ({ ...m, error: event.message }));
          else if (event.type === 'done') patchMessage(reply.id, (m) => ({ ...m, usage: event.usage, allowance: event.allowance || null }));
        },
      });
    } catch (err) {
      if (err && err.name !== 'AbortError') patchMessage(reply.id, (m) => ({ ...m, error: err.message || 'The assistant is unavailable right now.' }));
    } finally {
      patchMessage(reply.id, (m) => ({ ...m, pending: false }));
      setBusy(false);
      abortRef.current = null;
    }
  }, [patchMessage]);

  /**
   * Send words, with photos if any. `images` are attachments from
   * capture/images.js: { name, media_type, data, previewUrl }.
   */
  const send = useCallback(async (text, images = []) => {
    const words = String(text || '').trim();
    if ((!words && images.length === 0) || busy) return;
    setOpen(true);
    setHint(null);
    const userMsg = { id: nextId(), role: 'user', text: words, images: images.map((i) => ({ name: i.name, previewUrl: i.previewUrl })) };
    if (images.length) photosRef.current.set(userMsg.id, images);
    setDraftState('');
    clearDraft(userId);
    await runReply([...messages, userMsg], images);
  }, [busy, messages, runReply, userId]);

  /** The reply before `replyId`'s, and the person's message it answered. */
  const turnOf = useCallback((replyId) => {
    const i = messages.findIndex((m) => m.id === replyId);
    if (i < 1 || messages[i - 1].role !== 'user') return null;
    return { i, userMsg: messages[i - 1] };
  }, [messages]);

  /** A reply that failed: ask again with the same words and photos. */
  const retry = useCallback((replyId) => {
    const turn = turnOf(replyId);
    if (!turn || busy) return;
    runReply(messages.slice(0, turn.i), photosRef.current.get(turn.userMsg.id) || []);
  }, [busy, messages, runReply, turnOf]);

  /** A reply that failed: take the words back into the composer to change them. */
  const editFailed = useCallback((replyId) => {
    const turn = turnOf(replyId);
    if (!turn || busy) return;
    setMessages(messages.slice(0, turn.i - 1));
    setDraftState((d) => (d.trim() ? d : turn.userMsg.text || ''));
  }, [busy, messages, turnOf]);

  /**
   * The newest reply can be asked for again while nothing from it has been
   * saved; a saved card would be proposed twice.
   */
  const canRegenerate = useCallback((replyId) => {
    const last = messages[messages.length - 1];
    if (!last || last.id !== replyId || last.role !== 'assistant' || last.pending || busy) return false;
    if (!turnOf(replyId)) return false;
    return !(last.proposals || []).some((p) => p.state === 'applied' || p.state === 'failed');
  }, [busy, messages, turnOf]);

  const regenerate = useCallback((replyId) => {
    if (!canRegenerate(replyId)) return;
    const turn = turnOf(replyId);
    const old = messages[turn.i];
    for (const p of old.proposals || []) if (p.id && p.state === 'pending') reportProposalOutcome(p.id, 'rejected').catch(() => {});
    runReply(messages.slice(0, turn.i), photosRef.current.get(turn.userMsg.id) || []);
  }, [canRegenerate, messages, runReply, turnOf]);

  /** A tick on a simple card, kept with the message so closing the drawer keeps it. */
  const setProposalAccepted = useCallback((messageId, key, accepted) => {
    patchMessage(messageId, (m) => ({ ...m, proposals: m.proposals.map((p) => (p.key === key ? { ...p, accepted } : p)) }));
  }, [patchMessage]);

  /**
   * One field fixed on a pending card before it is applied. Throws, with the
   * reason, when the value cannot be saved, so the field stays open.
   */
  const editProposal = useCallback((messageId, key, field, value) => {
    const message = messagesRef.current.find((m) => m.id === messageId);
    const proposal = message && message.proposals.find((p) => p.key === key);
    if (!proposal || proposal.state !== 'pending') throw new Error('This card has already been decided.');
    editProposalField(proposal, field, value);
    patchMessage(messageId, (m) => ({ ...m, proposals: m.proposals.map((p) => (p.key === key ? editProposalField(p, field, value) : p)) }));
  }, [patchMessage]);

  /** Take applied cards back, newest first, so a contact goes before the account it was added to. */
  const undoApplied = useCallback(async (messageId, keys) => {
    const message = messagesRef.current.find((m) => m.id === messageId);
    if (!message) return;
    const touched = new Set();
    const targets = message.proposals.filter((p) => keys.includes(p.key) && canUndo(p)).reverse();
    let undone = 0;
    for (const proposal of targets) {
      try {
        touched.add(await undoProposal(proposal));
        undone += 1;
        patchMessage(messageId, (m) => ({ ...m, proposals: m.proposals.map((p) => (p.key === proposal.key ? { ...p, state: 'undone' } : p)) }));
        if (proposal.id) reportProposalOutcome(proposal.id, 'undone').catch(() => {});
      } catch (err) {
        toastRef.current.error(`Could not undo "${proposal.title}": ${errorText(err, 'it may have changed since')}`);
      }
    }
    if (undone) toastRef.current.info(undone === 1 ? 'Undone' : `Undid ${undone} changes`);
    await Promise.all([...touched].map((name) => invalidateEntity(queryClient, name)));
  }, [patchMessage, queryClient]);

  /** The meeting card as the person has edited it, kept until they approve or discard it. */
  const saveMeetingDraft = useCallback((messageId, key, report) => {
    patchMessage(messageId, (m) => ({ ...m, proposals: m.proposals.map((p) => (p.key === key ? { ...p, draftPayload: report } : p)) }));
  }, [patchMessage]);

  const stop = useCallback(() => { if (abortRef.current) abortRef.current.abort(); }, []);
  const clear = useCallback(() => { stop(); setMessages([]); }, [stop]);
  const toggle = useCallback(() => setOpen((o) => !o), []);
  const close = useCallback(() => setOpen(false), []);
  /**
   * Open the drawer; with words, send them (the default) or just put them in
   * the composer. `hint: 'note'` opens it ready for what happened.
   */
  const openWith = useCallback((text, { send: sendNow = true, hint: nextHint = null } = {}) => {
    setOpen(true);
    setHint(nextHint);
    setFocusTick((n) => n + 1);
    if (!text) return;
    if (sendNow) send(text);
    else setDraftState(String(text));
  }, [send]);

  /** The person decided on a message's cards: apply the accepted ones, record every outcome. */
  const decide = useCallback(async (messageId, decisions) => {
    const message = messages.find((m) => m.id === messageId);
    if (!message) return;
    const touched = new Set();
    const applied = [];
    for (const proposal of message.proposals) {
      if (proposal.state !== 'pending' || proposal.kind === 'link' || proposal.kind === 'meeting_report') continue;
      const accepted = decisions[proposal.key] !== false;
      if (!accepted) {
        patchMessage(messageId, (m) => ({ ...m, proposals: m.proposals.map((p) => (p.key === proposal.key ? { ...p, state: 'rejected' } : p)) }));
        if (proposal.id) reportProposalOutcome(proposal.id, 'rejected').catch(() => {});
        continue;
      }
      try {
        const result = await applyProposal(proposal, { user });
        if (result.entity) touched.add(result.entity);
        const done = { id: result.id, path: result.path };
        patchMessage(messageId, (m) => ({ ...m, proposals: m.proposals.map((p) => (p.key === proposal.key ? { ...p, state: 'applied', applied: done } : p)) }));
        applied.push({ ...proposal, state: 'applied', applied: done });
        // Edited: the AI was close, and the person fixed it. Which fields
        // they fixed is what tells the prompt where it goes wrong.
        if (proposal.id) {
          const outcome = proposal.edited ? reportProposalOutcome(proposal.id, 'edited', { fields: proposal.editedFields || [] }) : reportProposalOutcome(proposal.id, 'accepted');
          outcome.catch(() => {});
        }
      } catch (err) {
        const reason = errorText(err, 'Could not apply');
        patchMessage(messageId, (m) => ({ ...m, proposals: m.proposals.map((p) => (p.key === proposal.key ? { ...p, state: 'failed', error: reason } : p)) }));
      }
    }
    await Promise.all([...touched].map((name) => invalidateEntity(queryClient, name)));
    const undoable = applied.filter(canUndo).map((p) => p.key);
    if (undoable.length) {
      const line = applied.length === 1 ? `Saved: ${applied[0].title}` : `Saved ${applied.length} changes`;
      toastRef.current.undo(line, () => undoApplied(messageId, undoable));
    }
  }, [messages, patchMessage, queryClient, undoApplied, user]);

  /**
   * A meeting write-up approved from its card, as the person edited it:
   * contacts, colleagues, the Meeting, the action items, the attendees.
   * `base` is the card's idempotency root, so a retry replays safely.
   */
  const applyMeeting = useCallback(async (messageId, key, report, base) => {
    const message = messages.find((m) => m.id === messageId);
    const proposal = message && message.proposals.find((p) => p.key === key);
    if (!proposal) return null;
    const result = await applyMeetingReport({ ...proposal, payload: report }, { api, user, base });
    const ok = !!result.activity;
    const error = ok ? null : ((result.errors[0] && result.errors[0].message) || 'Could not save the meeting');
    patchMessage(messageId, (m) => ({ ...m, proposals: m.proposals.map((p) => (p.key === key ? { ...p, payload: report, state: ok ? 'applied' : 'failed', error, result: { activity_id: ok ? result.activity.id : null, created: result.created, errors: result.errors } } : p)) }));
    if (ok && proposal.id) {
      reportProposalOutcome(proposal.id, 'accepted', { activity_id: result.activity.id, contacts: result.created.contacts.length, internal_people: result.created.internal_people.length, tasks: result.created.tasks.length, errors: result.errors.length }).catch(() => {});
    }
    if (ok) await Promise.all(['activities', 'tasks', 'contacts'].map((name) => invalidateEntity(queryClient, name)));
    return result;
  }, [messages, patchMessage, queryClient, user]);

  /** One card put aside without applying it. */
  const dismissProposal = useCallback((messageId, key) => {
    const message = messages.find((m) => m.id === messageId);
    const proposal = message && message.proposals.find((p) => p.key === key);
    if (!proposal || proposal.state !== 'pending') return;
    patchMessage(messageId, (m) => ({ ...m, proposals: m.proposals.map((p) => (p.key === key ? { ...p, state: 'rejected' } : p)) }));
    if (proposal.id) reportProposalOutcome(proposal.id, 'rejected').catch(() => {});
  }, [messages, patchMessage]);

  const value = useMemo(() => ({ open, messages, busy, draft, setDraft, width, setWidth, toggle, close, openWith, send, stop, clear, decide, applyMeeting, dismissProposal, retry, editFailed, regenerate, canRegenerate, setProposalAccepted, saveMeetingDraft, editProposal, viewing, clearViewing, restoreViewing, hint, focusTick }),
    [open, messages, busy, draft, setDraft, width, setWidth, toggle, close, openWith, send, stop, clear, decide, applyMeeting, dismissProposal, retry, editFailed, regenerate, canRegenerate, setProposalAccepted, saveMeetingDraft, editProposal, viewing, clearViewing, restoreViewing, hint, focusTick]);
  return <AssistantContext.Provider value={value}>{children}</AssistantContext.Provider>;
}

export function useAssistant() {
  return useContext(AssistantContext) || NO_CONTEXT;
}
